import { useMemo } from "react";

export interface ToolCall {
  type: string;
  arguments: string;
  output?: string;
}

export const useExecutedToolsSnippets = (tool_calls: ToolCall[]) => {
  const snippets = useMemo(
    () => extractSnippets(tool_calls || []),
    [tool_calls],
  );

  return { snippets };
};

type ParsedArticle = {
  title?: string;
  url?: string;
  content?: string;
  score?: number;
  domain?: string;
};

const titleRegex = /Title:\s*(.*)/;
const urlRegex = /URL:\s*(.*)/;
const contentRegex = /Content:\s*([\s\S]*?)Score:/;
const scoreRegex = /Score:\s*([\d.]+)/;

function parseSearchResults(raw: string): ParsedArticle[] {
  const blocks = raw.split(/(?=Title: )/g); // Split into article blocks
  const results: ParsedArticle[] = [];

  for (const block of blocks) {
    const titleMatch = block.match(titleRegex);
    const urlMatch = block.match(urlRegex);
    const contentMatch = block.match(contentRegex);
    const scoreMatch = block.match(scoreRegex);

    const title = titleMatch?.[1]?.trim();
    const url = urlMatch?.[1]?.trim();
    const domain = url?.split("/")[2];
    const content = contentMatch?.[1]?.trim();
    const score = scoreMatch
      ? Number.parseFloat(scoreMatch[1] ?? "0")
      : undefined;

    if (title || url || content || score !== undefined) {
      results.push({ title, url, content, score, domain });
    }
  }

  return results;
}

function extractSnippets(tool_calls: ToolCall[]): ParsedArticle[] {
  const searchResults = tool_calls.filter((call) => call.type === "search");
  if (!searchResults) {
    return [];
  }
  const output = searchResults.map((call) => call.output).join("\n");
  const results = parseSearchResults(output);

  // Remove duplicates based on URL
  const uniqueResults = results.reduce((acc: ParsedArticle[], curr) => {
    if (!acc.some((item) => item.url === curr.url)) {
      acc.push(curr);
    }
    return acc;
  }, []);

  return uniqueResults;
}

export type Snippet = ParsedArticle;
